import { useState } from 'react';

interface ContentInsight {
  id: string;
  lineNumber: number;
  originalText: string;
  issue: string;
  explanation: string;
  suggestion: string;
  improvedVersion: string;
  category: 'ai-citation-boost' | 'discovery-optimization' | 'authority-building' | 'structure-enhancement' | 'readability-enhancement' | 'semantic-enrichment' | 'user-engagement' | 'technical-accuracy' | 'conversion-optimization' | 'accessibility-improvement';
}

interface ContentAnalysis {
  contentType: string;
  insights: ContentInsight[];
  chatbotResponse: string;
  analysisMetadata?: {
    processingTime: number;
    confidenceScore: number;
    researchBasis: string[];
    uniqueFindings: number;
    realTimeResearch: boolean;
    aiIntelligenceLevel: string;
  };
}

interface AnalysisPanelProps {
  analysis: ContentAnalysis | null;
  isAnalyzing: boolean;
  onApplyImprovement: (lineNumber: number, improvedText: string) => void;
}

export function AnalysisPanel({ analysis, isAnalyzing, onApplyImprovement }: AnalysisPanelProps) {
  const [expandedInsight, setExpandedInsight] = useState<string | null>(null);

  // Debug logging
  console.log('🔍 AnalysisPanel received:', { analysis, isAnalyzing });
  console.log('🔍 Analysis type:', typeof analysis);
  console.log('🔍 Analysis has insights:', analysis?.insights?.length || 0);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'ai-citation-boost':
        return (
          <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'authority-building':
        return (
          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'structure-enhancement':
        return (
          <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'discovery-optimization':
        return (
          <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        );
      case 'user-engagement':
        return (
          <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'technical-accuracy':
        return (
          <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
        );
      case 'conversion-optimization':
        return (
          <svg className="w-4 h-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        );
      case 'accessibility-improvement':
        return (
          <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'ai-citation-boost':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'authority-building':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'structure-enhancement':
        return 'bg-purple-50 border-purple-200 text-purple-800';
      case 'discovery-optimization':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'user-engagement':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      case 'technical-accuracy':
        return 'bg-indigo-50 border-indigo-200 text-indigo-800';
      case 'conversion-optimization':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'accessibility-improvement':
        return 'bg-pink-50 border-pink-200 text-pink-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  if (isAnalyzing) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full">
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.001 8.001 0 01-7.625-5.781L2 20l3.219-6.781A8.001 8.001 0 0121 12z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">AI Content Analyzer</h3>
              <p className="text-sm text-gray-500">Analyzing your content...</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
              <span className="text-gray-600">Performing line-by-line analysis...</span>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-gray-600">Analyzing content structure</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse animation-delay-300"></div>
                  <span className="text-sm text-gray-600">Evaluating AI discoverability</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-pulse animation-delay-600"></div>
                  <span className="text-sm text-gray-600">Generating specific improvements</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full">
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.001 8.001 0 01-7.625-5.781L2 20l3.219-6.781A8.001 8.001 0 0121 12z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">AI Content Analyzer</h3>
              <p className="text-sm text-gray-500">Ready to analyze</p>
            </div>
          </div>

          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-500 mb-2">Paste your content and click "Analyze Content"</p>
            <p className="text-sm text-gray-400">I'll provide detailed, line-by-line suggestions to optimize your content for AI discovery</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full">
      <div className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-3.582 8-8 8a8.001 8.001 0 01-7.625-5.781L2 20l3.219-6.781A8.001 8.001 0 0121 12z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {analysis.analysisMetadata?.realTimeResearch ? '🤖 Real AI Content Analyzer' : 'AI Content Analyzer'}
            </h3>
            <p className="text-sm text-gray-500">
              {analysis.contentType} • {analysis.insights.length} insights
              {analysis.analysisMetadata?.realTimeResearch && (
                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                  Live AI + Web Research
                </span>
              )}
            </p>
            {analysis.analysisMetadata && (
              <p className="text-xs text-gray-400 mt-1">
                {analysis.analysisMetadata.aiIntelligenceLevel} • 
                {Math.round(analysis.analysisMetadata.confidenceScore * 100)}% confidence • 
                {analysis.analysisMetadata.processingTime}ms
              </p>
            )}
          </div>
        </div>

        {/* Chatbot Response */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="prose prose-sm text-gray-700">
            {analysis.chatbotResponse.split('\n').map((paragraph, index) => {
              if (paragraph.trim().startsWith('**') && paragraph.trim().endsWith('**')) {
                return (
                  <h4 key={index} className="font-semibold text-gray-900 mb-2">
                    {paragraph.replace(/\*\*/g, '')}
                  </h4>
                );
              } else if (paragraph.trim().startsWith('🎯') || paragraph.trim().startsWith('📝') || paragraph.trim().startsWith('🏗️') || paragraph.trim().startsWith('💡')) {
                return (
                  <p key={index} className="font-medium text-gray-800 mb-2">
                    {paragraph}
                  </p>
                );
              } else if (paragraph.trim()) {
                return (
                  <p key={index} className="mb-2">
                    {paragraph}
                  </p>
                );
              }
              return null;
            })}
          </div>
        </div>

        {/* Insights List */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Line-by-Line Insights</h4>
          
          {analysis.insights.map((insight) => (
            <div
              key={insight.id}
              className={`border rounded-lg overflow-hidden transition-all duration-200 ${
                expandedInsight === insight.id ? 'shadow-md' : 'shadow-sm'
              }`}
            >
              <div
                className="p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedInsight(
                  expandedInsight === insight.id ? null : insight.id
                )}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getCategoryColor(insight.category)}`}>
                      {getCategoryIcon(insight.category)}
                      <span className="ml-1">Line {insight.lineNumber}</span>
                    </span>
                    <div>
                      <h5 className="font-medium text-gray-900">{insight.issue}</h5>
                      <p className="text-sm text-gray-600 mt-1">{insight.explanation}</p>
                    </div>
                  </div>
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${
                      expandedInsight === insight.id ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {expandedInsight === insight.id && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  <div className="space-y-4">
                    <div>
                      <h6 className="text-sm font-medium text-gray-900 mb-2">Original Text:</h6>
                      <div className="bg-white border border-gray-200 rounded p-3 text-sm text-gray-700">
                        {insight.originalText}
                      </div>
                    </div>

                    <div>
                      <h6 className="text-sm font-medium text-gray-900 mb-2">Suggested Improvement:</h6>
                      <div className="bg-primary-50 border border-primary-200 rounded p-3 text-sm text-gray-700">
                        {insight.improvedVersion}
                      </div>
                    </div>

                    <div>
                      <h6 className="text-sm font-medium text-gray-900 mb-2">Why This Helps:</h6>
                      <p className="text-sm text-gray-600">{insight.suggestion}</p>
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="text-xs text-gray-500">
                        Click to replace line {insight.lineNumber} with improved version
                      </div>
                      <button
                        onClick={() => {
                          onApplyImprovement(insight.lineNumber, insight.improvedVersion);
                          // Visual feedback that improvement was applied
                          const button = document.activeElement as HTMLButtonElement;
                          if (button) {
                            const originalText = button.textContent;
                            button.textContent = '✓ Applied!';
                            button.className = button.className.replace('bg-primary-500', 'bg-green-500');
                            setTimeout(() => {
                              button.textContent = originalText;
                              button.className = button.className.replace('bg-green-500', 'bg-primary-500');
                            }, 2000);
                          }
                        }}
                        className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center space-x-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <span>Apply This Improvement</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {analysis.insights.length === 0 && (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-green-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-600 font-medium">Great job! Your content is well-optimized for AI discovery.</p>
              <p className="text-sm text-gray-500 mt-1">No specific improvements needed at this time.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}